import {Button} from 'antd'
import {exam_generateExaminationPaperForExportFile_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect, useRef} from 'react'
import {IoCheckmarkCircleOutline} from 'react-icons/io5'
import NoFoundPage from 'views/NoFoundPage'
import {getResponseItem, NullAsDefault, renderOwnChild} from 'views/Shared'
import {AppEntities} from '../enums'
import ExaminationContext, {ExaminationProvider,} from '../Exam/ExaminationContext'
import {bindQueryParam} from '../functions/routerHelper'
import {notifyOnError} from './createErrorEvent'
import CourseContext, {CourseProvider} from "../Course/CourseContext";
import {
    course_generateAttendanceExportFile_Api,
    course_generateExportFile_Api,
    course_generateGradeExportFile_Api
} from "../../../apis";
import {renderIf, renderIfElse} from "../../Shared";
import {HiInboxArrowDown} from "react-icons/hi2";
import {LayoutContext} from "../../../components/layouts/Default/LayoutContext";
import {renderFromToTime} from "../../../helpers/datetimeHelpers";
import LoadingBlock from "../../Loading/LoadingBlock";

const ExportContainer = ({item, query, apiInfo, values, children}) => {
    const t = useTranslate()

    const {
        response,
        isLoading,
        handleAsyncAction: handleExport,
    } = useAsyncAction({
        query,
        values,
        apiInfo,
        onError: notifyOnError(t),
    })

    if (_.isFunction(children)) {
        return children(handleExport, {
            isLoading,
            ...getResponseItem(response),
        })
    } else {
        return children || null
    }
}

const Downloader = ({
                        array,
                        children,
                        downloadURL,
                        delay = 3000,
                        auto = false,
                        onSuccess = NullAsDefault,
                    }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const timer = useRef()

    useEffect(() => {
        if (!!downloadURL && auto) {
            if (timer.current) {
                clearTimeout(timer.current)
            }
            timer.current = setTimeout(() => {
                onSuccess({downloadURL})
            }, delay)
        }
        return () => {
            if (timer.current) {
                clearTimeout(timer.current)
            }
        }
    }, [downloadURL, auto])

    const renderActions = (child) => {
        if (_.isEmpty(array)) {
            return child
        }
        return Array.from(array || []).map((path, index) => (
            <div
                key={index}
                className="w-full flex items-center gap-2 p-2 md:p-3 background border border-color-50 rounded-lg">
                <div className="flex flex-col flex-1 truncate">
          <span className="text-xs text-color-400 italic">
            {`${t('file')} :`}
          </span>
                    <a
                        className="font-semibold text-sm italic truncate no-underline"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={path}>
                        {_.last(_.split(path, '/'))}
                    </a>
                </div>
                <Button className="button-rounded-md no-border" type="primary">
                    <a
                        className="flex flex-center no-underline"
                        rel="noopener noreferrer"
                        target="_blank"
                        href={path}>
                        {isSm ? <HiInboxArrowDown/> : t('download')}
                    </a>
                </Button>
            </div>
        ))
    }

    return (
        <>
            {!!downloadURL && (
                <div className="mt-3 mb-6 border-2 border-green-500 rounded-lg">
                    <div className="flex items-center gap-2 p-3">
                        <IoCheckmarkCircleOutline
                            size={30}
                            className="text-green-500"
                        />
                        <div className="flex flex-col items-start gap-1">
              <span className="font-bold text-green-600 leading-tight">
                {`${t('the data has been generate')} !`}
              </span>
                            <span className="font-light text-xs text-green-500 italic">
                {t(
                    'please click the {download} button below to download to your device',
                    {
                        download: (
                            <span className="font-bold text-green-700">
                        {` ${t('download')} `}
                      </span>
                        ),
                    }
                )}
              </span>
                        </div>
                    </div>
                </div>
            )}
            <div
                className={classNames(
                    _.isEmpty(array) ? 'flex justify-center' : 'flex flex-col'
                )}>
                {!!downloadURL
                    ? renderActions(
                        <Button
                            className="no-shadow no-text-shadow rounded-lg"
                            type="primary">
                            <a
                                className="no-underline"
                                rel="noopener noreferrer"
                                target="_blank"
                                href={downloadURL}>
                                {isSm ? <HiInboxArrowDown/> : t('download')}
                            </a>
                        </Button>
                    )
                    : children || null}
            </div>
        </>
    )
}

const ExportModalContent = ({title, item, Wrapper = renderOwnChild, ...props}) => {

    const t = useTranslate()
    const {time_open, time_close} = item ?? {}

    if (!item || _.isEmpty(item)) {
        return <NoFoundPage/>
    }

    return (
        <Wrapper>
            <div className="flex flex-col w-full">
                <div className="flex flex-col gap-2">
                    <span className="text-color-300 uppercase font-medium space-x-1">
                        {t('export data')}{' '}{renderIfElse(title, t(title), t(getType(item)))}
                    </span>
                    <div className="p-3 background border border-color-50 shadow-md rounded-md">
                        <div className="flex flex-col gap-1 flex-1">
                            <span className="font-bold text-color-000 text-xs tracking-wide">
                                {getTitle(item)}
                            </span>
                            <span className="font-semibold text-color-100 text-xs italic">
                                <span className="font-normal text-color-500 mr-1">
                                    {t('organized by')}
                                </span>
                                {_.get(item, 'owner.title')}
                            </span>
                            {renderIf(
                                _.some([time_open, time_close]),
                                <span className="font-semibold text-slate-600 text-xs italic">
                                    <span className="font-normal text-color-500 mr-1">
                                        {`${t('time takes place')} : `}
                                    </span>
                                    {renderFromToTime(time_open, time_close)}
                                </span>
                            )}

                        </div>
                    </div>
                </div>
                <ExportContainer
                    item={item}
                    {...props}>
                    {(handleExport, {path, paths, isLoading}) => (
                        <Downloader
                            array={paths}
                            downloadURL={path}>
                            <Button
                                type="primary"
                                className="button-rounded-lg no-border mt-6"
                                disabled={!!isLoading}
                                onClick={() => {
                                    handleExport()
                                }}>
                                {t('export data')}
                            </Button>
                        </Downloader>
                    )}
                </ExportContainer>
            </div>
        </Wrapper>
    )
}

export const createExaminationExporter = (item) => (
    <ExportModalContent
        {...{
            title: 'examination',
            item,
            query: bindQueryParam({
                id: getId(item),
            }),
            apiInfo: exam_generateExaminationPaperForExportFile_Api,
        }}
    />
)

export const createCourseStudentExporter = (item) => (
    <ExportModalContent
        {...{
            title: 'students of the course',
            item,
            query: bindQueryParam({
                id: getId(item),
            }),
            apiInfo: course_generateExportFile_Api,
        }}
    />
)
export const createCourseAttendanceExporter = (item) => (
    <ExportModalContent
        {...{
            title: 'attendance for the course',
            item,
            query: bindQueryParam({
                id: getId(item),
            }),
            apiInfo: course_generateAttendanceExportFile_Api,
        }}
    />
)
export const createCourseGradeExporter = (item) => (
    <ExportModalContent
        {...{
            title: 'grade for the course',
            item,
            query: bindQueryParam({
                id: getId(item),
            }),
            apiInfo: course_generateGradeExportFile_Api,
        }}
    />
)

export const ExporterLoader = ({id, type}) => {
    if (_.isEmpty(id)) {
        return null
    }
    switch (type) {
        case AppEntities.EXAMINATION:
            return (
                <ExaminationProvider item={id}>
                    <ExaminationContext.Consumer>
                        {({examination, isLoading}) =>
                            !!isLoading ? (
                                <LoadingBlock/>
                            ) : (
                                createExaminationExporter(examination)
                            )
                        }
                    </ExaminationContext.Consumer>
                </ExaminationProvider>
            )
        case "COURSE_STUDENT_EXPORT":
            return (
                <CourseProvider item={id}>
                    <CourseContext.Consumer>
                        {({item, isLoading}) =>
                            !!isLoading ? (
                                <LoadingBlock/>
                            ) : (
                                createCourseStudentExporter(item)
                            )
                        }
                    </CourseContext.Consumer>
                </CourseProvider>
            )
        case "COURSE_ATTENDANCE_EXPORT":
            return (
                <CourseProvider item={id}>
                    <CourseContext.Consumer>
                        {({item, isLoading}) =>
                            !!isLoading ? (
                                <LoadingBlock/>
                            ) : (
                                createCourseAttendanceExporter(item)
                            )
                        }
                    </CourseContext.Consumer>
                </CourseProvider>
            )
        case "COURSE_GRADE_EXPORT":
            return (
                <CourseProvider item={id}>
                    <CourseContext.Consumer>
                        {({item, isLoading}) =>
                            !!isLoading ? (
                                <LoadingBlock/>
                            ) : (
                                createCourseGradeExporter(item)
                            )
                        }
                    </CourseContext.Consumer>
                </CourseProvider>
            )

        default:
            return null
    }
}
